import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Bell, Sparkles, MessageCircle, Heart, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';

const NotificationsPage = () => {
  const { user } = useAuth();
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!user) return;
    const { data } = await supabase.from('notifications')
      .select('*').eq('user_id', user.id)
      .order('created_at', { ascending: false }).limit(50);
    setNotifs(data || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    if (!user) return;
    const ch = supabase.channel(`notif-page-${user.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` }, load)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
    // eslint-disable-next-line
  }, [user]);

  const markAllRead = async () => {
    await supabase.from('notifications').update({ read: true }).eq('user_id', user.id).eq('read', false);
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="heading-display text-2xl font-bold">Avisos</h1>
          <p className="text-xs text-slate-400 mt-0.5">Tudo o que acontece na sua jornada</p>
        </div>
        <button onClick={markAllRead} className="text-[11px] text-[#4facfe] hover:text-white">Marcar tudo como lido</button>
      </div>

      {loading && <div className="py-12 text-center text-sm text-slate-400">Carregando...</div>}

      {!loading && notifs.length === 0 && (
        <div className="glass-card rounded-3xl p-10 text-center">
          <Bell size={32} className="mx-auto text-slate-500 mb-3" />
          <div className="text-sm font-bold">Sem avisos por enquanto</div>
          <p className="text-xs text-slate-400 mt-2">Você vai ver aqui suas Sparks, mensagens e oportunidades.</p>
        </div>
      )}

      <div className="space-y-2">
        {notifs.map((n) => {
          const isSpark = n.type === 'spark_accepted' || n.type === 'new_spark';
          const Icon = n.type === 'new_message' ? MessageCircle : isSpark ? Sparkles : n.type === 'business_liked' ? Heart : Bell;
          return (
            <motion.div key={n.id} initial={{opacity:0, y:6}} animate={{opacity:1, y:0}}
              className={`relative rounded-2xl p-4 flex items-start gap-3 ${isSpark ? 'glass-card-gold' : 'glass-card'} ${!n.read ? 'ring-1 ring-[#4facfe]/30' : ''}`}>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${isSpark ? 'bg-[#ffa726]/20 text-[#ffd54f]' : 'bg-[#4facfe]/15 text-[#4facfe]'}`}>
                <Icon size={16}/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-semibold text-white">{n.title}</div>
                  <div className="text-[10px] text-slate-500">{timeAgo(n.created_at)}</div>
                </div>
                {n.body && <div className="text-xs text-slate-400 mt-0.5">{n.body}</div>}
                {n.data?.spark_id && (n.type === 'spark_accepted' || n.type === 'new_message') && (
                  <Link to={`/app/chat/${n.data.spark_id}`} className="inline-flex items-center gap-1 text-[11px] text-[#4facfe] mt-2 hover:text-white">Abrir conversa →</Link>
                )}
                {n.data?.spark_id && n.type === 'new_spark' && (
                  <Link to="/app/sparks" className="inline-flex items-center gap-1 text-[11px] text-[#ffd54f] mt-2 hover:text-white">Ver Sparks →</Link>
                )}
              </div>
              {!n.read && <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-[#4facfe]" />}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

const timeAgo = (iso) => {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff/60000);
  if (m < 1) return 'agora';
  if (m < 60) return `${m}min`;
  const h = Math.floor(m/60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h/24);
  return `${d}d`;
};

export default NotificationsPage;
